import { createApiClient } from '@/types/portal-api.generated'
import { getCurrentEnvironment } from '@/utilities/getEnvironment'
import { serializeQueryParams } from '@/utilities/api/querySerializer'
import { getPortalApiBaseUrl } from '@/utilities/api/portalApiBaseUrl'
import {
  applyPortalApiRequestHeaders,
  clearIdentityCache,
} from '@/utilities/api/requestInterceptors'
import { clearPostSignupAuthUserIdBridge } from '@/utilities/auth/postSignupAuthUserIdBridge'
import { clearTrustedIdentityScopesCache } from '@/utilities/auth/trustedIdentityScopes'

export { withAuthHeaders } from '@/utilities/api/requestInterceptors'

/**
 * Clears the cached x-user-identity. Call during logout so the next user does not
 * receive a stale identity.
 */
export const clearAuthUserId = (): void => {
  if (globalThis.window === undefined) return

  clearIdentityCache()
  clearTrustedIdentityScopesCache()
  clearPostSignupAuthUserIdBridge()
  try {
    localStorage.removeItem('authUserId')
  } catch {
    // Ignore storage cleanup failures in non-browser or restricted contexts.
  }
}

export const portalApiClient = createApiClient(getPortalApiBaseUrl(), {
  // Don't validate responses - we don't want to error in prod if Portal API
  // deploys a change that is backwards-compatible at runtime but we haven't
  // updated the generated types yet, especially in dev. In prod, we are even
  // more lenient - if it works at runtime, there is no need to error for
  // strictness' sake.
  validate: getCurrentEnvironment() === 'production' ? 'none' : 'request',
  axiosConfig: {
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    paramsSerializer: {
      serialize: serializeQueryParams,
    },
  },
})

/**
 * Request interceptor to automatically inject auth provider headers.
 *
 * This interceptor runs on every API request and adds x-auth-provider,
 * x-auth-provider-record-id, and x-auth-user-id headers when available.
 * This replaces the functionality that was previously provided by the
 * auth-proxy sidecar container in the Kubernetes deployment.
 *
 * The headers are silently added when available - if no session exists
 * (e.g., on unauthenticated pages), the request proceeds without them.
 *
 * For logged-in users, internal_id is read from the JWT and sent as x-auth-user-id.
 *
 * When talking to a local portal-api (Portal API base URL contains localhost), also
 * fetches and injects x-user-identity from /dev/identity/from-jwt.
 */
portalApiClient.axios.interceptors.request.use(async (config) => {
  await applyPortalApiRequestHeaders(config)
  return config
})

export type Mutable<T> = {
  -readonly [P in keyof T]: T[P]
}

export type GetQueryParams<T extends keyof typeof portalApiClient> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (typeof portalApiClient)[T] extends (...args: any[]) => any
    ? // If there is a body, queries will be in the second parameter
      Parameters<(typeof portalApiClient)[T]>[1] extends { queries: infer Q }
      ? Mutable<Q>
      : // Otherwise, queries will be in the first parameter
        NonNullable<
            NonNullable<Parameters<(typeof portalApiClient)[T]>[0]>['queries']
          > extends never
        ? never
        : Mutable<NonNullable<NonNullable<Parameters<(typeof portalApiClient)[T]>[0]>['queries']>>
    : never
